<template>
  <v-card>
    <v-card-text>
      <v-row>
        <v-col>
          <v-select v-model="filters.account" :error="accountError" :items="accounts" :loading="loading"
            item-text="account_name" item-value="name" menu-props="auto" label="Conta" hide-details single-line solo
            color="primary" dense clearable></v-select>
        </v-col>
        <v-col>
          <v-autocomplete autocomplete="off" v-model="filters.location" :error="locationError"
            :items="locations && locations.data" :loading="loadingLocations" :search-input.sync="searchLocation"
            v-on:keyup="onLocationKeyUp" item-text="location_name_code" item-value="name" menu-props="auto"
            label="Local ou Id" hide-details single-line solo color="primary" dense></v-autocomplete>
        </v-col>
        <v-col>
          <date-picker solo hide-details clearable label="De" noIcon dense v-on:change="changeSchedulingDateStart" />
        </v-col>
        <v-col>
          <date-picker solo hide-details clearable label="Até" noIcon dense :min="filters.date_start"
            v-on:change="changeSchedulingDateEnd" />
        </v-col>
      </v-row>

      <p class="mt-8 text--primary">Escolha os campos do relatório</p>

      <v-row>
        <v-col class="ma-0 pa-0 px-2" md="4" sm="4" v-for="(name, field) in fields" :key="field">
          <v-checkbox v-model="filters.fields[field]" small :label="name" value="1" />
        </v-col>
      </v-row>
    </v-card-text>

    <v-snackbar v-model="snackbar.show" timeout="3000" :color="snackbar.color">
      {{ snackbar.text }}

      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackbar.show = false">
          Ok
        </v-btn>
      </template>
    </v-snackbar>

    <v-card-actions class="pa-4">
      <v-btn :loading="loadingReport" color="primary" @click="generateReport" small>
        Gerar relatório
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapState } from 'vuex'
import { get } from 'lodash'
import { handleResponseError } from '@/api/handleError'
import DatePicker from '@/components/DatePicker'

export default {
  name: 'ReviewsReport',
  computed: {
    ...mapState({
      reportData: state => state.reviews.reportData,
      locations: state => state.locations.all,
      reportExport: state => state.reviews.reportExport,
    }),
  },
  methods: {
    showNotification: function (message, color) {
      this.snackbar.text = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
    changeSchedulingDateStart(date) {
      this.filters.date_start = date
    },
    changeSchedulingDateEnd(date) {
      this.filters.date_end = date
    },
    onLocationKeyUp() {
      clearTimeout(this.timeoutLocations)
      this.timeoutLocations = setTimeout(this.fetchLocations, 400)
    },
    validate() {
      this.accountError = false
      this.locationError = false

      if (!this.filters.account) {
        this.accountError = true
        this.showNotification('O campo conta é obrigatório.', 'red darken-2')
        return false
      }
      if (!this.filters.date_start) {
        this.showNotification('A data inicial é obrigatória.', 'red darken-2')
        return false
      }
      if (!this.filters.date_end) {
        this.showNotification('A data final é obrigatória.', 'red darken-2')
        return false
      }
      if (this.filters.date_start > this.filters.date_end) {
        this.showNotification('A data final inicial não pode ser maior que a data final.', 'red darken-2')
        return false
      }
      let validateFields = Object.values(this.filters.fields).filter(value => value != null);
      if (!validateFields || (Array.isArray(validateFields) && validateFields.length === 0)) {
        this.showNotification('Escolha ao menos um campo.', 'red darken-2')
        return false
      }
      return true
    },
    async getReportData() {
      this.loading = true
      await this.$store.dispatch('reviews/getReportData').catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar os dados.')
        this.showNotification(message, 'red darken-2')
        this.loading = false
      })
      this.loading = false
    },
    async fetchLocations() {
      if (!this.filters.account) return
      this.loadingLocations = true
      await this.$store.dispatch('locations/getLocations', {
        account: this.filters.account,
        search: this.searchLocation,
        limit: 100,
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar os locais.')
        this.showNotification(message, 'red darken-2')
        this.loadingLocations = false
      })
      this.loadingLocations = false
    },
    async fetchKeywords() {
      if (!this.filters.account) return
      await this.$store.dispatch('keywords/getKeywords', {
        account: this.filters.account,
      }).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao buscar as palavras chave.')
        this.showNotification(message, 'red darken-2')
      })
    },
    async generateReport() {
      if (!this.validate()) return
      this.loadingReport = true
      await this.$store.dispatch('reviews/exportReport', this.filters).catch(e => {
        const message = handleResponseError(e, 'Ocorreu um erro ao gerar o relatório.')
        this.showNotification(message, 'red darken-2')
        this.loadingReport = false
      })
      this.loadingReport = false
    },
  },
  components: {
    DatePicker,
  },
  watch: {
    reportData(data) {
      this.fields = get(data, 'fields', [])
      this.accounts = get(data, 'accounts.data', [])
    },
    locations(locations) {
      this.locationsData = get(locations, 'data')
    },
    'filters.account': function () {
      this.searchLocation = ''
      this.fetchLocations()
      this.fetchKeywords()
    },
    reportExport(data, oldData) {
      if (data && data !== oldData) {
        if (get(data, 'tasks[0].id')) {
          this.$router.push(`/task/${get(data, 'tasks[0].id')}`)
        } else {
          this.showNotification('Nenhuma tarefa foi criada.', 'red darken-2')
        }
      }
    }
  },
  created() {
    this.getReportData()
  },
  destroyed() {
    this.$store.dispatch('locations/clearLocations')
  },
  data: () => ({
    fields: [],
    accounts: [],
    locationsData: [],
    locationError: null,
    loading: false,
    loadingReport: false,
    loadingLocations: false,
    searchLocation: '',
    timeoutLocations: null,
    accountError: false,
    filters: {
      account: null,
      location: null,
      date_start: null,
      date_end: null,
      fields: {},
    },
    snackbar: {
      text: '',
      show: false,
      color: 'info',
    },
  }),
}
</script>

<style scoped></style>
